import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock, Facebook, Instagram, MapPin } from "lucide-react";
import ServiceAreaMap from "@/web/components/service-area-map";

const SERVICE_AREAS = [
  "Ruakaka",
  "Waipu",
  "Langs Beach",
  "Mangawhai Heads",
  "Mangawhai",
  "Te Arai",
  "Pakiri",
  "Leigh",
  "Matakana",
  "Omaha",
  "Warkworth",
  "Snells Beach",
  "Orewa",
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About | Cool Wave — Certified Mangawhai HVAC Specialists</title>
        <meta
          name="description"
          content="Cool Wave is a Mangawhai-based, Certified Panasonic Installer with 20+ years keeping Northland and Auckland homes warm, dry and healthy."
        />
      </Helmet>

      <section className="relative overflow-hidden bg-background">
        <div className="relative min-h-[420px] md:min-h-[480px] lg:min-h-[520px]">
          <img
            src="/images/hero-about.jpg"
            alt="Mangawhai coast — pohutukawa, ocean and white-sand beach"
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/55 to-ink/0" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-transparent to-transparent" />
          <div className="container relative flex min-h-[420px] items-center md:min-h-[480px] lg:min-h-[520px]">
            <div className="max-w-2xl py-12 md:py-16">
              <span className="mb-5 inline-block rounded-full bg-white/95 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700 shadow-sm backdrop-blur">
                Mangawhai · 20+ years
              </span>
              <h1 className="mb-5 text-4xl font-bold leading-[1.05] tracking-tight text-white [text-shadow:_0_2px_16px_rgb(0_0_0_/_45%)] md:text-5xl lg:text-6xl">
                About Cool Wave.
              </h1>
              <p className="max-w-xl text-lg text-white/95 [text-shadow:_0_1px_8px_rgb(0_0_0_/_55%)]">
                Local. Certified. Backed by two decades of keeping Northland homes warm, dry and healthy.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-[linear-gradient(to_bottom,#EAF6FE_0%,#EAF6FE_97%,#FFFFFF_100%)]">
      <section className="py-12 md:py-16">
        <div className="container max-w-3xl">
          <p className="text-lg text-body">
            Gavin started Cool Wave over 20 years ago to keep Mangawhai and Northland homes warm, dry and comfortable.
            Today we're a Certified Panasonic Installer, working with every major brand to design, install and look after
            systems built to last.
          </p>
        </div>
      </section>

      <section className="container py-14 md:py-16">
        <div className="grid gap-10 md:grid-cols-3">
          <div className="md:col-span-1">
            <img
              src="/images/gavin.jpg"
              alt="Gavin, owner and lead installer at Cool Wave, with his child by the water"
              className="block w-full rounded-xl border-[3px] border-black"
              loading="lazy"
            />
            <div className="mt-3 text-center">
              <div className="text-base font-semibold text-ink">Gavin</div>
              <div className="text-sm text-body">Owner &amp; Lead Installer</div>
            </div>
          </div>

          <div className="md:col-span-2">
            <h2 className="mb-3 text-2xl font-bold tracking-tight">Service area</h2>
            <ul className="grid grid-cols-2 gap-y-1 text-sm text-body">
              {SERVICE_AREAS.map((area) => (
                <li key={area} className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-brand" /> {area}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="container">
          <div className="mb-6 max-w-2xl">
            <h2 className="mb-2 text-3xl font-bold tracking-tight md:text-4xl">Where we work</h2>
            <p className="text-lg text-body">Based in Mangawhai, servicing the east-coast corridor from Ruakaka in the north down to Orewa in the south — everything east of State Highway 1.</p>
          </div>
          <div className="overflow-hidden rounded-xl border border-border/60 bg-white">
            <ServiceAreaMap />
          </div>
          <p className="mt-3 flex items-center gap-2 text-xs text-body/70">
            <span className="inline-block h-0.5 w-6 border-t-2 border-dashed border-red-600" aria-hidden />
            Red dashed boundary shows our service area.
          </p>
        </div>
      </section>

      <section className="container pb-6 pt-4">
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-border/60 bg-white px-6 py-10 text-center shadow-sm md:py-12">
          <h2 className="max-w-2xl text-3xl font-bold tracking-tight md:text-4xl">Ready to get started?</h2>
          <p className="max-w-xl text-lg text-body">Tell us about your space and we'll come back with an honest, no-pressure quote within one business day.</p>
          <Link
            to="/free-quote"
            className="inline-flex h-12 items-center justify-center rounded-md bg-brand px-8 text-base font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-brand-600 hover:shadow-xl"
          >
            Get a Free Quote
          </Link>
        </div>
      </section>

      <section className="container pb-16 pt-14 md:pb-20 md:pt-16">
        <h2 className="mb-10 text-center text-3xl font-bold tracking-tight md:text-4xl">Get in touch</h2>
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <a href="tel:0272980080" className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border border-border/60 bg-white p-5 text-center hover:border-brand">
            <Phone className="h-7 w-7 text-brand" />
            <div>
              <div className="text-xs uppercase tracking-wider text-body/60">Phone</div>
              <div className="whitespace-nowrap text-lg font-semibold text-ink">027 298 0080</div>
            </div>
          </a>
          <a href="mailto:gavin@coolwave.co.nz" className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border border-border/60 bg-white p-5 text-center hover:border-brand">
            <Mail className="h-7 w-7 text-brand" />
            <div>
              <div className="text-xs uppercase tracking-wider text-body/60">Email</div>
              <div className="whitespace-nowrap text-lg font-semibold text-ink">gavin@coolwave.co.nz</div>
            </div>
          </a>
          <div className="flex items-start gap-4 rounded-xl border border-border/60 bg-white p-7">
            <Clock className="mt-0.5 h-7 w-7 flex-none text-brand" />
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wider text-body/60">Hours</div>
              <div className="text-base font-semibold text-ink md:text-lg">8am–5pm Mon–Fri</div>
              <div className="mt-1.5 text-sm text-body/70">After-hours urgent — please enquire.</div>
            </div>
          </div>
          <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-white p-7">
            <div className="text-xs uppercase tracking-wider text-body/60">Social</div>
            <a href="https://www.facebook.com/coolwavenz" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-base font-semibold text-ink hover:text-brand md:text-lg">
              <Facebook className="h-6 w-6 flex-none text-brand" /> Facebook
            </a>
            <a href="https://www.instagram.com/coolwavenz" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-base font-semibold text-ink hover:text-brand md:text-lg">
              <Instagram className="h-6 w-6 flex-none text-brand" /> Instagram
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
